// src/views/Homepage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { publicSiteWebService } from "../services/publicSiteWebService";
import { urlimage } from "../utils/config";

export default function Homepage() {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    parametres: null,
    fonctionnalites: [],
    statistiques: [],
    temoignages: [],
    contact: null,
  });

  const [typedText, setTypedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);

  // Particules générées une seule fois (sinon elles changeraient à chaque
  // frappe de l'effet machine à écrire)
  const particles = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        delay: `${i * 0.3}s`,
        size: `${2 + Math.random() * 4}px`,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        alpha: 0.05 + Math.random() * 0.08,
      })),
    []
  );

  // Chargement des données depuis l'API
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await publicSiteWebService.getPublicData();
        setPageData({
          parametres: data.parametres,
          fonctionnalites: data.fonctionnalites || [],
          statistiques: data.statistiques || [],
          temoignages: data.temoignages || [],
          contact: data.contact,
        });

        // Utiliser le slogan du backend ou le texte par défaut
        const slogan = data.parametres?.slogan || "L'application qui rapproche l'école des familles";
        startTypingEffect(slogan);
      } catch (error) {
        // Fallback: utiliser le texte par défaut
        startTypingEffect("L'application qui rapproche l'école des familles");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Effet de machine à écrire
  const startTypingEffect = (text) => {
    let index = 0;
    setIsTyping(true);
    setTypedText("");

    const timer = setInterval(() => {
      if (index <= text.length) {
        setTypedText(text.slice(0, index));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(timer);
      }
    }, 40);

    return () => clearInterval(timer);
  };

  // Récupérer les fonctionnalités mises en avant ou les 6 premières
  const displayedFeatures = pageData.fonctionnalites.length > 0
    ? pageData.fonctionnalites
        .filter(f => f.mise_en_avant_accueil || f.actif)
        .slice(0, 6)
    : [];

  // Récupérer les statistiques
  const displayedStats = pageData.statistiques.filter(s => s.actif).slice(0, 4);

  // Récupérer les témoignages actifs (limités à 3)
  const activeTemoignages = pageData.temoignages.filter(t => t.actif).slice(0, 3);

  // Récupérer les coordonnées de contact
  const contactInfo = pageData.contact || {};

  // Récupérer les paramètres
  const params = pageData.parametres || {};

  // Badge texte
  const badgeText = params.badge_texte || "Nouvelle version 2.0";

  // Logo - Utiliser urlimage pour le chemin complet
  const logoUrl = params.logo ? `${urlimage}${params.logo}` : null;

  // Couleurs (thème Kamalpha par défaut ; le back-office peut les surcharger)
  const primaryColor = params.couleur_primaire || "#1B4F80";
  const secondaryColor = params.couleur_secondaire || "#2F6DA8";

  // Couleurs pour les fonctionnalités
  const featureColors = [
    primaryColor,
    secondaryColor,
    "#C08A2E",
    "#0f6e56",
    "#3b6d11",
    "#8A5A14",
  ];

  // Si chargement, afficher un placeholder
  if (loading) {
    return (
      <div className="accueil-page">
        <PublicTopBar />
        <div className="loader-container" style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div className="loader-spinner" style={{
            width: 48, height: 48,
            border: `4px solid ${primaryColor}20`,
            borderTopColor: primaryColor,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite"
          }} />
        </div>
        <style>{`
          .loader-container { display: flex; justify-content: center; align-items: center; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  return (
    <div className="accueil-page">
      <PublicTopBar />

      {/* Hero Section */}
      <section className="hero-section" style={{
        background: `linear-gradient(135deg, #f8fafc 0%, ${primaryColor}08 100%)`
      }}>
        <div className="hero-bg-gradient" />
        <div className="hero-particles">
          {particles.map((p, i) => (
            <div
              key={i}
              className="particle"
              style={{
                '--delay': p.delay,
                '--size': p.size,
                left: p.left,
                top: p.top,
                background: `rgba(27, 79, 128, ${p.alpha})`
              }}
            />
          ))}
        </div>
        <div className="hero-container">
          <div className="hero-content">
            {/* {badgeText && (
              <div className="hero-badge">
                <span className="badge-pulse" />
                <i className="ti ti-school" />
                <span>{badgeText}</span>
              </div>
            )} */}

            <h1 className="hero-title">
              {params.nom_plateforme || "Suivi Scolaire"}
              <span className="hero-title-accent">.</span>
            </h1>

            <p className="hero-subtitle">
              {typedText}
              <span className={`cursor ${!isTyping ? 'blink' : ''}`}>|</span>
            </p>

            <div className="hero-actions">
              <Link to="/contact" className="btn btn-primary" style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              }}>
                <i className="ti ti-user-plus" />
                Commencer maintenant
                <i className="ti ti-arrow-right" />
              </Link>
              <Link to="/features" className="btn btn-ghost">
                <i className="ti ti-play-circle" />
                En savoir plus
              </Link>
            </div>

            {displayedStats.length > 0 && (
              <div className="hero-stats">
                {displayedStats.map((stat, index) => (
                  <React.Fragment key={index}>
                    <div className="hero-stat">
                      <span className="hero-stat-value">{stat.valeur}</span>
                      <span className="hero-stat-label">{stat.libelle}</span>
                    </div>
                    {index < displayedStats.length - 1 && (
                      <div className="hero-stat-divider" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <div className="hero-image">
            <div className="dash-mockup">
              <div className="dash-window">
                <div className="dash-titlebar">
                  <div className="dash-dots">
                    <span style={{ background: '#ef4444' }} />
                    <span style={{ background: '#f59e0b' }} />
                    <span style={{ background: '#10b981' }} />
                  </div>
                  <div className="dash-url">
                    <i className="ti ti-lock" />
                    {(params.nom_plateforme || "suivi-scolaire").toLowerCase().replace(/\s+/g, '-')}.app/dashboard
                  </div>
                </div>

                <div style={{ display: 'flex', width: '100%' }}>
                  {/* Sidebar (reproduit la sidebar admin : bleu profond + or) */}
                  <div style={{ width: 108, flexShrink: 0, background: 'linear-gradient(180deg, #0E2F52, #1B4F80)', padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, marginBottom: 10 }}>
                      <div style={{ width: 26, height: 26, borderRadius: 6, background: '#fff', border: '1px solid rgba(192, 138, 46, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="ti ti-book-2" style={{ fontSize: 14, color: '#1B4F80' }} /></div>
                      <div style={{ fontSize: 8, fontWeight: 500, color: '#fff' }}>Suivi scolaire</div>
                      <div style={{ fontSize: 5.5, color: '#E3B75A', letterSpacing: 0.3 }}>espace admin</div>
                    </div>
                    {[
                      ['ti-home', 'Accueil', false],
                      ['ti-layout-dashboard', 'Dashboard', true],
                      ['ti-school', 'Pédagogique', false],
                      ['ti-users', 'Élèves', false],
                      ['ti-user-cog', 'Utilisateurs', false],
                      ['ti-messages', 'Message', false],
                      ['ti-bell', 'Notification', false],
                      ['ti-calendar-check', 'Présence', false],
                    ].map(([icon, label, active]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 6px', borderRadius: 4, background: active ? 'rgba(255,255,255,0.14)' : 'transparent', color: '#fff', fontSize: 7, fontWeight: active ? 600 : 400, boxShadow: active ? 'inset 2px 0 0 #C08A2E' : 'none', opacity: active ? 1 : 0.85 }}>
                        <i className={`ti ${icon}`} style={{ fontSize: 9, color: active ? '#E3B75A' : '#fff' }} />{label}
                      </div>
                    ))}
                    <div style={{ flex: 1 }} />
                    <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 6 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#0E2F52', border: '1px solid #C08A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 8, fontWeight: 500 }}>S</div>
                      <div style={{ fontSize: 6.5, color: '#fff' }}>seydou</div>
                    </div>
                  </div>

                  {/* Main */}
                  <div style={{ flex: 1, background: '#f8fafc', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <i className="ti ti-layout-dashboard" style={{ fontSize: 11, color: '#C08A2E' }} />
                        <span style={{ fontSize: 9, fontWeight: 500, color: '#1B4F80' }}>vue d'ensemble</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 7, color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: 4, padding: '3px 6px' }}>
                        <i className="ti ti-refresh" style={{ fontSize: 8 }} />Actualiser
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                      {[
                        ['ti-circle-check', '#10b981', 96, "Présents aujourd'hui"],
                        ['ti-clock', '#f59e0b', 5, "En retard aujourd'hui"],
                        ['ti-circle-x', '#ef4444', 22, "Absents aujourd'hui"],
                        ['ti-users', '#3b82f6', 123, 'Total élèves'],
                        ['ti-mail', '#8b5cf6', 3, 'Messages non lus'],
                        ['ti-credit-card', '#f59e0b', 2, 'Abonnements expirant'],
                      ].map(([icon, color, value, label], i) => (
                        <div key={label} className="dash-stat" style={{ background: '#fff', borderLeft: `3px solid ${color}`, borderRadius: 5, padding: '6px 7px', animationDelay: `${i * 0.08}s` }}>
                          <i className={`ti ${icon}`} style={{ fontSize: 10, color }} />
                          <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a', marginTop: 1 }}>{value}</div>
                          <div style={{ fontSize: 6.5, color: '#94a3b8' }}>{label}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 6 }}>
                      <div>
                        <div style={{ fontSize: 6.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.02, color: '#94a3b8', marginBottom: 4 }}>Accès rapide</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                          {[
                            ['ti-users', '#3b82f6', 'Élèves'],
                            ['ti-calendar-time', '#0d9488', 'Planning'],
                            ['ti-notebook', '#f59e0b', 'Notes'],
                            ['ti-file-certificate', '#2563eb', 'Bulletins'],
                            ['ti-fingerprint', '#ef4444', 'Pointage'],
                            ['ti-id', '#8b5cf6', 'Cartes'],
                            ['ti-messages', '#06b6d4', 'Messages'],
                            ['ti-bell-ringing', '#f97316', 'Notifs'],
                          ].map(([icon, color, label]) => (
                            <div key={label} style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 6, padding: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <div style={{ width: 15, height: 15, borderRadius: 4, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className={`ti ${icon}`} style={{ fontSize: 8, color: '#fff' }} /></div>
                              <div style={{ fontSize: 5.5, color: '#64748b' }}>{label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 6.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.02, color: '#94a3b8', marginBottom: 4 }}>À traiter</div>
                        <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 6, padding: '5px 6px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {[
                            ['ti-clock', "5 retard(s)", 5],
                            ['ti-file-alert', 'Justificatifs', 1],
                            ['ti-credit-card', 'Abonnements', 2],
                          ].map(([icon, label, count]) => (
                            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 6.5, color: '#0f172a' }}>
                              <i className={`ti ${icon}`} style={{ fontSize: 9, color: '#ef4444' }} />
                              <span style={{ flex: 1 }}>{label}</span>
                              <span style={{ background: '#ef4444', color: '#fff', borderRadius: 6, padding: '0 4px', fontSize: 6 }}>{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 6 }}>
                      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 6, padding: '6px 8px' }}>
                        <div style={{ fontSize: 7, color: '#94a3b8', marginBottom: 5 }}>Présence sur 7 jours</div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 46 }}>
                          {[[30, 8, 45], [15, 6, 65], [40, 10, 35], [12, 5, 70], [25, 9, 50], [18, 7, 60], [22, 8, 55]].map(([r, a, g], i) => (
                            <div key={i} className="dash-bar-col" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', animationDelay: `${i * 0.06}s` }}>
                              <div style={{ height: `${r}%`, background: '#ef4444', borderRadius: '2px 2px 0 0' }} />
                              <div style={{ height: `${a}%`, background: '#f59e0b' }} />
                              <div style={{ height: `${g}%`, background: '#10b981', borderRadius: '0 0 2px 2px' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ fontSize: 7, color: '#94a3b8', alignSelf: 'flex-start', marginBottom: 3 }}>Par classe</div>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'conic-gradient(#3b82f6 0% 22%, #10b981 22% 40%, #f59e0b 40% 55%, #8b5cf6 55% 68%, #06b6d4 68% 82%, #ef4444 82% 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ width: 21, height: 21, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 7, fontWeight: 500, color: '#0f172a' }}>96</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-scroll-indicator">
          <span>Défiler</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* Features Section */}
      {displayedFeatures.length > 0 && (
        <section className="features-section">
          <div className="features-container">
            <div className="features-header">
              <span className="features-badge">Fonctionnalités</span>
              <h2 className="features-title">Une solution complète pour l'éducation</h2>
              <p className="features-desc">
                Découvrez tous les outils qui facilitent la gestion scolaire et la communication
              </p>
            </div>

            <div className="features-grid">
              {displayedFeatures.map((feature, index) => (
                <div key={feature.id || index} className="feature-card" style={{
                  animationDelay: `${index * 100}ms`
                }}>
                  <div className="feature-icon" style={{
                    background: `${feature.couleur || featureColors[index % featureColors.length]}15`,
                    color: feature.couleur || featureColors[index % featureColors.length]
                  }}>
                    <i className={`ti ${feature.icone || 'ti-box'}`} />
                  </div>
                  <h3 className="feature-title">{feature.titre}</h3>
                  <p className="feature-desc">{feature.description}</p>
                  <Link to="/features" className="feature-link">
                    En savoir plus
                    <i className="ti ti-arrow-right" />
                  </Link>
                </div>
              ))}
            </div>

            <div className="features-footer">
              <Link to="/features" className="btn btn-primary btn-lg" style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              }}>
                <i className="ti ti-list" />
                Voir toutes les fonctionnalités
                <i className="ti ti-arrow-right" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Témoignages Section */}
      {activeTemoignages.length > 0 && (
        <section className="testimonials-section">
          <div className="testimonials-container">
            <div className="testimonials-header">
              <span className="testimonials-badge">Témoignages</span>
              <h2 className="testimonials-title">Ce que disent nos utilisateurs</h2>
              <p className="testimonials-desc">
                Découvrez les retours d'expérience de nos utilisateurs satisfaits
              </p>
            </div>

            <div className="testimonials-grid">
              {activeTemoignages.map((temoignage, index) => (
                <div key={temoignage.id || index} className="testimonial-card" style={{
                  animationDelay: `${index * 150}ms`
                }}>
                  <div className="testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={`star ${i < (temoignage.note || 5) ? 'filled' : ''}`}>★</span>
                    ))}
                  </div>
                  <p className="testimonial-text">"{temoignage.texte}"</p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar" style={{
                      background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
                    }}>
                      {temoignage.auteur?.charAt(0) || "U"}
                    </div>
                    <div>
                      <div className="testimonial-name">{temoignage.auteur}</div>
                      <div className="testimonial-role">
                        {temoignage.role}
                        {temoignage.etablissement ? ` · ${temoignage.etablissement}` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container" style={{
          background: `linear-gradient(135deg, ${primaryColor}08, ${secondaryColor}06)`
        }}>
          <div className="cta-content">
            <h2 className="cta-title">
              Prêt à révolutionner le suivi scolaire ?
            </h2>
            <p className="cta-desc">
              Rejoignez des milliers d'établissements qui font confiance à {params.nom_editeur || "Enumera"}
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary btn-lg" style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              }}>
                <i className="ti ti-user-plus" />
                Commencer maintenant
              </Link>
              <Link to="/demo" className="btn btn-ghost btn-lg">
                <i className="ti ti-video" />
                Voir la démo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

        /* Palette Kamalpha (dupliquée ici : AdminLayout n'est pas monté hors connexion) */
        .accueil-page {
          --pr:    #1B4F80;
          --pr-lt: #2F6DA8;
          --pr-dk: #0E2F52;
          --pr-bg: #E6EEF7;
          --or:    #C08A2E;
          --or-lt: #E3B75A;
          --or-dk: #8A5A14;

          min-height: 100vh;
          background: #ffffff;
          color: #0f172a;
          overflow: hidden;
        }

        .hero-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 100px 32px 60px;
          overflow: hidden;
        }

        .hero-bg-gradient {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 20% 50%, rgba(27, 79, 128, 0.07) 0%, transparent 60%),
            radial-gradient(ellipse at 80% 50%, rgba(192, 138, 46, 0.05) 0%, transparent 60%);
          pointer-events: none;
        }

        .hero-particles {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          border-radius: 50%;
          animation: float-particle 8s ease-in-out infinite;
          animation-delay: var(--delay);
          width: var(--size);
          height: var(--size);
        }

        @keyframes float-particle {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
          50% { transform: translate(30px, -40px) scale(1.5); opacity: 0.8; }
        }

        .hero-container {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          max-width: 1280px;
          width: 100%;
          align-items: center;
        }

        .hero-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          width: fit-content;
          padding: 6px 16px 6px 10px;
          background: var(--pr-bg);
          border: 1px solid rgba(192, 138, 46, 0.35);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          color: var(--pr);
        }

        .badge-pulse {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--or);
          animation: pulse-dot 2s ease-in-out infinite;
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }

        .hero-title {
          font-size: 56px;
          font-weight: 800;
          line-height: 1.1;
          margin: 0;
          letter-spacing: -0.02em;
          color: #0f172a;
        }

        .hero-title-accent {
          color: var(--or);
        }

        .hero-subtitle {
          font-size: 20px;
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        .cursor {
          color: var(--or);
          font-weight: 300;
        }

        .blink {
          animation: blink 1s step-end infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        .hero-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 15px;
          text-decoration: none;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--pr), var(--pr-lt));
          color: #ffffff;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(27, 79, 128, 0.3);
        }

        .btn-ghost {
          background: rgba(15, 23, 42, 0.04);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-ghost:hover {
          background: var(--pr-bg);
          border-color: var(--or);
        }

        .btn-lg {
          padding: 14px 32px;
          font-size: 16px;
        }

        .hero-stats {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 16px 24px;
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(15, 23, 42, 0.06);
          border-radius: 16px;
          margin-top: 4px;
          flex-wrap: wrap;
        }

        .hero-stat {
          display: flex;
          flex-direction: column;
        }

        .hero-stat-value {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
        }

        .hero-stat-label {
          font-size: 12px;
          color: #94a3b8;
        }

        .hero-stat-divider {
          width: 1px;
          height: 30px;
          background: rgba(15, 23, 42, 0.08);
        }

        .hero-image {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        /* === DASHBOARD MOCKUP === */
        .dash-mockup {
          position: relative;
          width: 100%;
          max-width: 480px;
          animation: dash-float 6s ease-in-out infinite;
        }

        @keyframes dash-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .dash-window {
          width: 100%;
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 30px 90px rgba(14, 47, 82, 0.22);
        }

        .dash-titlebar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          background: #f1f5f9;
          border-bottom: 1px solid rgba(15, 23, 42, 0.06);
        }

        .dash-dots {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
        }

        .dash-dots span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: block;
        }

        .dash-url {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 6px;
          background: #ffffff;
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 10px;
          color: #94a3b8;
          border: 1px solid rgba(15, 23, 42, 0.06);
        }

        .dash-url i {
          font-size: 10px;
        }

        .dash-stat {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.06);
          border-left-width: 3px;
          border-left-style: solid;
          border-radius: 8px;
          padding: 8px 6px;
          animation: fadeUp 0.6s ease both;
        }

        .hero-scroll-indicator {
          position: absolute;
          bottom: 32px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: rgba(15, 23, 42, 0.3);
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .scroll-line {
          width: 1px;
          height: 30px;
          background: linear-gradient(to bottom, var(--or), transparent);
          animation: scroll-pulse 2s ease-in-out infinite;
        }

        @keyframes scroll-pulse {
          0%, 100% { opacity: 0.3; transform: scaleY(1); }
          50% { opacity: 1; transform: scaleY(1.5); }
        }

        /* === FEATURES SECTION === */
        .features-section {
          padding: 80px 32px;
          background: #ffffff;
        }

        .features-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .features-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .features-badge {
          display: inline-block;
          padding: 4px 16px;
          background: var(--pr-bg);
          color: var(--pr);
          border-radius: 12px;
          box-shadow: inset 0 -2px 0 var(--or);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .features-title {
          font-size: 36px;
          font-weight: 700;
          margin: 12px 0 8px;
          color: #0f172a;
        }

        .features-desc {
          font-size: 16px;
          color: #64748b;
          max-width: 560px;
          margin: 0 auto;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }

        .feature-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 28px 24px 24px;
          text-align: center;
          transition: all 0.3s ease;
          animation: fadeUp 0.6s ease both;
          display: flex;
          flex-direction: column;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          background: #ffffff;
          border-color: rgba(192, 138, 46, 0.45);
          box-shadow: 0 12px 40px rgba(14, 47, 82, 0.08);
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .feature-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin: 0 auto 12px;
        }

        .feature-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 8px;
          color: #0f172a;
        }

        .feature-desc {
          font-size: 13px;
          color: #64748b;
          margin: 0 0 16px;
          line-height: 1.6;
          flex: 1;
        }

        .feature-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: var(--pr);
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
          justify-content: center;
          padding: 6px 0;
          border-top: 1px solid rgba(192, 138, 46, 0.25);
          margin-top: auto;
        }

        .feature-link i {
          font-size: 14px;
          transition: transform 0.2s ease;
        }

        .feature-link:hover {
          color: var(--or-dk);
        }

        .feature-link:hover i {
          transform: translateX(4px);
        }

        .features-footer {
          text-align: center;
          margin-top: 8px;
        }

        /* === TESTIMONIALS SECTION === */
        .testimonials-section {
          padding: 80px 32px;
          background: #f8fafc;
        }

        .testimonials-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .testimonials-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .testimonials-badge {
          display: inline-block;
          padding: 4px 16px;
          background: var(--pr-bg);
          color: var(--pr);
          border-radius: 12px;
          box-shadow: inset 0 -2px 0 var(--or);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .testimonials-title {
          font-size: 36px;
          font-weight: 700;
          margin: 12px 0 8px;
          color: #0f172a;
        }

        .testimonials-desc {
          font-size: 16px;
          color: #64748b;
          max-width: 560px;
          margin: 0 auto;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .testimonial-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 28px 24px;
          transition: all 0.3s ease;
          animation: fadeUp 0.6s ease both;
          display: flex;
          flex-direction: column;
        }

        .testimonial-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(14, 47, 82, 0.08);
          border-color: rgba(192, 138, 46, 0.4);
        }

        .testimonial-stars {
          display: flex;
          gap: 2px;
          margin-bottom: 12px;
        }

        .testimonial-stars .star {
          font-size: 18px;
          color: #e2e8f0;
          transition: color 0.2s ease;
        }

        .testimonial-stars .star.filled {
          color: #F59E0B;
        }

        .testimonial-text {
          font-size: 15px;
          line-height: 1.7;
          color: #0f172a;
          margin: 0 0 16px;
          font-style: italic;
          flex: 1;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: auto;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }

        .testimonial-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--pr), var(--pr-lt));
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 16px;
          flex-shrink: 0;
        }

        .testimonial-name {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
        }

        .testimonial-role {
          font-size: 12px;
          color: #94a3b8;
        }

        /* === CTA SECTION === */
        .cta-section {
          padding: 80px 32px;
          background: #f8fafc;
        }

        .cta-container {
          position: relative;
          overflow: hidden;
          max-width: 1280px;
          margin: 0 auto;
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          padding: 56px 40px;
          text-align: center;
        }

        /* Filet bleu → or : la signature du logo */
        .cta-container::before {
          content: "";
          position: absolute;
          left: 0; right: 0; top: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--pr) 0%, var(--pr-lt) 55%, var(--or) 100%);
        }

        .cta-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 12px;
          color: #0f172a;
        }

        .cta-desc {
          font-size: 16px;
          color: #64748b;
          margin: 0 0 24px;
        }

        .cta-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* === RESPONSIVE === */
        @media (max-width: 1024px) {
          .hero-container {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .hero-content {
            align-items: center;
          }

          .hero-title {
            font-size: 44px;
          }

          .hero-stats {
            justify-content: center;
          }

          .hero-actions {
            justify-content: center;
          }

          .hero-image {
            order: -1;
          }

          .dash-mockup {
            max-width: 420px;
          }

          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .testimonials-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .hero-section {
            padding: 80px 20px 40px;
          }

          .hero-title {
            font-size: 36px;
          }

          .hero-subtitle {
            font-size: 17px;
          }

          .hero-stats {
            flex-direction: column;
            gap: 12px;
            padding: 16px;
          }

          .hero-stat-divider {
            display: none;
          }

          .dash-mockup {
            max-width: 340px;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
          }

          .features-section {
            padding: 60px 20px;
          }

          .testimonials-section {
            padding: 60px 20px;
          }

          .cta-section {
            padding: 60px 20px;
          }

          .cta-container {
            padding: 36px 24px;
          }

          .cta-title {
            font-size: 26px;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 28px;
          }

          .dash-mockup {
            max-width: 280px;
          }

          .btn {
            font-size: 13px;
            padding: 10px 18px;
          }

          .btn-lg {
            font-size: 14px;
            padding: 12px 24px;
          }

          .features-title {
            font-size: 28px;
          }

          .testimonials-title {
            font-size: 28px;
          }

          .feature-card {
            padding: 20px 16px;
          }

          .testimonial-card {
            padding: 20px 16px;
          }
        }
      `}</style>
    </div>
  );
}